import { Component, DestroyRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { combineLatest, of, switchMap, take, tap } from 'rxjs';

import { TmtLoggerService } from 'tmt-logger';

import { EcuInformation } from 'src/models/ecuInformation';
import { FileService } from 'src/services/file.service';

import { FileParserService } from './../../../../services/file-parser.service';

@Component({
	selector: 'app-ecu-readout',
	templateUrl: './ecu-readout.component.html',
	styleUrls: ['./ecu-readout.component.scss'],
})
export class EcuReadoutComponent {
	private destroyRef = inject(DestroyRef);

	// For old test runs the data was extracted into the database tables, which hold the
	// authoritative, complete set of ECU readouts (and the S3 file, if any, is only a thinner
	// snapshot). For newer uploads the database is empty and the full file lives in S3.
	// The database is therefore queried first; if it has no readouts, the S3 file is parsed.
	public fileData$ = combineLatest([this.fileService.testRunUid$, this.fileService.xcomFiles$]).pipe(
		switchMap(([testRunUid, files]) => {
			return this.fileService.getEcuReadoutsFromDb$(testRunUid).pipe(
				take(1),
				switchMap(dbItems => {
					if (dbItems.length) {
						this.setDataSource(dbItems, false);
						return of(dbItems);
					}

					// No data in the database: fall back to parsing the S3 file if one exists.
					if (!files || !files.length) {
						this.setDataSource([], true);
						return of([]);
					}

					return this.fileService.getFileByFileUid$(files[0].FileUid, files[0].FileName).pipe(
						switchMap(file => (file ? this.fileService.getFilestringFromFile$(file) : of(''))),
						tap(fileString => {
							const items = this.fileParserService.parseXcom(fileString);
							this.setDataSource(items ?? [], true);
						}),
					);
				}),
			);
		}),
	);

	@Output() countEcuReadouts: EventEmitter<any> = new EventEmitter();

	/** Emits true when the readouts were loaded from the database rather than parsed from an S3 file. */
	@Output() readoutsFromDatabase: EventEmitter<boolean> = new EventEmitter();

	constructor(
		private fileService: FileService,
		private fileParserService: FileParserService,
		private loggerService: TmtLoggerService,
	) {
		// Subscribe to file data, since observables with Tegel are a pain. TODO: Replace with better alternative.
		this.fileData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	/** Columns shown when readouts are parsed from an S3 file, which includes a description. */
	private readonly fileColumns: string[] = ['ecu', 'id', 'description', 'value'];

	/** Columns shown when readouts come from the database, which has no description. */
	private readonly dbColumns: string[] = ['ecu', 'id', 'value'];

	displayedColumns: string[] = this.fileColumns;

	dataSource;

	countEcuReadoutFiles: number;

	maxall = 10;

	@ViewChild(MatPaginator) paginator: MatPaginator;

	@ViewChild(MatSort) sort: MatSort;

	/**
	 * Binds readouts to the table and emits the count.
	 * @param items Readouts to display.
	 * @param fromFile Whether the readouts were parsed from an S3 file (which has a description)
	 * or loaded from the database (which does not). Controls whether the Description column is shown.
	 */
	private setDataSource(items: EcuInformation[], fromFile: boolean) {
		this.displayedColumns = fromFile ? this.fileColumns : this.dbColumns;

		this.dataSource = new MatTableDataSource<EcuInformation>(items);
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;

		this.countEcuReadouts.emit(items.length);
		this.readoutsFromDatabase.emit(!fromFile);
	}

	public uniqueFilter(value, index, self) {
		return self.indexOf(value) === index;
	}

	public getPageSizeOptions(): number[] {
		if (this.dataSource && this.dataSource.paginator && this.dataSource.paginator.length > this.maxall) {
			return [10, 20, this.dataSource.paginator.length];
		} else {
			return [10, 20];
		}
	}

	public applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();
	}
}
